import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import jsQR from "jsqr";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, Camera, X, AlertCircle } from "lucide-react";

export default function Scanner() {
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);

  const [running, setRunning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [manualToken, setManualToken] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stopScan() {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch {}
      streamRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {}
      videoRef.current.srcObject = null;
    }
    if (mountedRef.current) setRunning(false);
  }

  async function startScan() {
    setError("");
    if (running || starting) return;
    setStarting(true);
    try {
      // HTTPS + getUserMedia guard
      if (!window.isSecureContext) {
        throw Object.assign(new Error("insecure"), { name: "InsecureContext" });
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw Object.assign(new Error("unsupported"), { name: "NotSupportedError" });
      }

      // Try environment (back) camera first, then fall back
      let stream;
      const attempts = [
        { video: { facingMode: { exact: "environment" } }, audio: false },
        { video: { facingMode: { ideal: "environment" } }, audio: false },
        { video: true, audio: false },
      ];
      let lastErr = null;
      for (const constraints of attempts) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (!stream) throw lastErr || new Error("No se pudo abrir la cámara");

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      // Important for iOS Safari + Android Chrome
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.setAttribute("muted", "true");
      video.setAttribute("autoplay", "true");
      video.muted = true;
      video.playsInline = true;
      video.srcObject = stream;

      // Wait for metadata (dimensions available) before starting decode loop
      await new Promise((resolve) => {
        if (video.readyState >= 1) return resolve();
        const onLoaded = () => {
          video.removeEventListener("loadedmetadata", onLoaded);
          resolve();
        };
        video.addEventListener("loadedmetadata", onLoaded);
      });

      try {
        await video.play();
      } catch (e) {
        // Some browsers require a user gesture — the button click already provides it.
        // If play() rejects, surface a friendly error.
        throw Object.assign(new Error("No se pudo reproducir el video de la cámara."), {
          name: "PlaybackError",
          cause: e,
        });
      }

      if (!mountedRef.current) {
        stopScan();
        return;
      }

      setRunning(true);
      // Start decoding loop
      rafRef.current = requestAnimationFrame(scanLoop);
    } catch (e) {
      let msg = "No se pudo iniciar la cámara.";
      switch (e?.name) {
        case "NotAllowedError":
        case "SecurityError":
          msg = "Permiso de cámara denegado. Habilítalo en los ajustes del navegador para este sitio.";
          break;
        case "NotFoundError":
        case "DevicesNotFoundError":
          msg = "No se encontró una cámara en este dispositivo.";
          break;
        case "NotReadableError":
        case "TrackStartError":
          msg = "La cámara está siendo usada por otra aplicación. Ciérrala e inténtalo de nuevo.";
          break;
        case "OverconstrainedError":
        case "ConstraintNotSatisfiedError":
          msg = "La cámara no soporta las opciones solicitadas.";
          break;
        case "InsecureContext":
          msg = "El escáner requiere una conexión segura (HTTPS).";
          break;
        case "NotSupportedError":
          msg = "Este navegador no soporta acceso a cámara.";
          break;
        case "PlaybackError":
          msg = "No se pudo reproducir el video de la cámara.";
          break;
        default:
          if (e?.message) msg = `No se pudo iniciar la cámara: ${e.message}`;
      }
      setError(msg);
      toast.error(msg);
      // Ensure any partial resources are freed
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        streamRef.current = null;
      }
    } finally {
      if (mountedRef.current) setStarting(false);
    }
  }

  function scanLoop() {
    if (!mountedRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streamRef.current) return;
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (w > 0 && h > 0) {
        // Downscale to at most 640 wide for perf on mobile
        const maxW = 640;
        const scale = Math.min(1, maxW / w);
        const cw = Math.round(w * scale);
        const ch = Math.round(h * scale);
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, cw, ch);
        const imgData = ctx.getImageData(0, 0, cw, ch);
        const code = jsQR(imgData.data, cw, ch, { inversionAttempts: "dontInvert" });
        if (code && code.data) {
          handleDecoded(code.data);
          return;
        }
      }
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }

  async function handleDecoded(text) {
    if (busyRef.current) return;
    busyRef.current = true;
    let token = text;
    try {
      const u = new URL(text);
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("qr");
      if (idx >= 0 && parts[idx + 1]) token = parts[idx + 1];
    } catch {
      // Not a URL — assume the raw text is the token
    }
    await lookup(token);
    busyRef.current = false;
  }

  async function lookup(token) {
    if (!token || busy) return;
    setBusy(true);
    stopScan();
    try {
      const { data } = await api.get(`/orders/by-qr/${encodeURIComponent(token)}`);
      toast.success(`Orden ${data.order_number}`);
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tight">Escanear QR</h1>
        <p className="text-slate-500 text-sm mt-1">
          Apunta la cámara al QR pegado en el equipo o pega el código manualmente.
        </p>
      </div>

      <div className="card-base p-4">
        <div className="relative w-full aspect-square bg-slate-50 rounded-xl overflow-hidden">
          {/* Always-mounted video element (React-controlled). Hidden until running. */}
          <video
            ref={videoRef}
            data-testid="scanner-video"
            playsInline
            muted
            autoPlay
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
              running ? "opacity-100" : "opacity-0"
            }`}
          />
          {/* Hidden canvas for frame decoding */}
          <canvas ref={canvasRef} className="hidden" />

          {!running && !error && !starting && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <ScanLine size={48} className="text-blue-600 mb-3" />
              <p className="text-slate-500 text-sm">Toca "Iniciar cámara" para escanear</p>
            </div>
          )}

          {starting && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="scanner-starting">
              <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-slate-600 text-sm">Iniciando cámara…</p>
            </div>
          )}

          {error && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
              data-testid="scanner-error"
            >
              <AlertCircle size={40} className="text-red-500 mb-3" />
              <p className="text-red-700 text-sm font-semibold">{error}</p>
              <p className="text-slate-500 text-xs mt-2">
                Puedes ingresar el código manualmente abajo.
              </p>
            </div>
          )}

          {running && (
            <>
              <div className="pointer-events-none absolute inset-8 border-2 border-blue-500 rounded-2xl shadow-[0_0_15px_rgba(37,99,235,0.15)]" />
              <div className="pointer-events-none absolute top-3 right-3 text-[10px] uppercase tracking-widest text-blue-700 font-mono bg-white/95 px-2 py-1 rounded">
                Escaneando…
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          {!running ? (
            <button
              onClick={startScan}
              disabled={starting}
              className="btn-primary flex-1"
              data-testid="scanner-start"
            >
              <Camera size={18} /> {starting ? "Iniciando…" : "Iniciar cámara"}
            </button>
          ) : (
            <button onClick={stopScan} className="btn-danger flex-1" data-testid="scanner-stop">
              <X size={18} /> Detener
            </button>
          )}
        </div>
      </div>

      <div className="card-base p-4">
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
          Ingresar código manualmente
        </div>
        <div className="flex gap-2">
          <input
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value.trim())}
            placeholder="Token del QR o URL"
            className="input-base"
            data-testid="scanner-manual-input"
          />
          <button
            onClick={() => {
              let token = manualToken;
              try {
                const u = new URL(manualToken);
                const parts = u.pathname.split("/").filter(Boolean);
                const idx = parts.indexOf("qr");
                if (idx >= 0 && parts[idx + 1]) token = parts[idx + 1];
              } catch {
                // Not a URL — assume raw token
              }
              lookup(token);
            }}
            className="btn-primary"
            data-testid="scanner-manual-lookup"
            disabled={!manualToken || busy}
          >
            Buscar
          </button>
        </div>
      </div>
    </div>
  );
}
